/**
 * Seed MOPA cut-parameter tables and helpers.
 *
 * These are community-reported STARTING POINTS for color marking on stainless.
 * MOPA colors are highly specific to machine wattage, lens/field size, focus
 * offset, material batch, and even ambient conditions — every value here must
 * be verified against a physical test grid before production. The disclaimer
 * string below is surfaced in the UI and the printable report.
 */
import type { CutParams, MaterialPreset } from "../core/types";
import { Q_PULSE_STEPS } from "../core/types";

export const PRESET_DISCLAIMER =
  "Starting points only — MOPA colors depend on your machine, lens, focus, and " +
  "material. Calibrate with a test grid before relying on any value.";

/** Scan/fill defaults shared by every layer (overridable per layer in the UI). */
export const DEFAULT_SCAN_PARAMS = {
  hatchAngleDeg: 0,
  crossHatch: false,
  bidir: true,
  fillAllAtOnce: false,
};

/** Neutral default parameters for a freshly-seen layer with no preset match. */
export const DEFAULT_CUT_PARAMS: CutParams = {
  power: 20,
  speed: 800,
  frequencyKhz: 500,
  qPulseNs: 6,
  intervalMm: 0.01,
  passes: 1,
  ...DEFAULT_SCAN_PARAMS,
};

function params(
  power: number,
  speed: number,
  frequencyKhz: number,
  qPulseNs: number,
  intervalMm: number,
  passes = 1,
): CutParams {
  return { power, speed, frequencyKhz, qPulseNs, intervalMm, passes, ...DEFAULT_SCAN_PARAMS };
}

/**
 * 100W MOPA / stainless starting table: speed and pulse held nearly constant,
 * color swept mostly by frequency and line interval.
 */
const PRESET_100W: MaterialPreset = {
  name: "100W stainless — frequency-swept",
  machine: "100W MOPA",
  material: "Stainless 304/316",
  colors: [
    { color: "#000000", label: "Black", params: params(30, 800, 300, 60, 0.003) },
    { color: "#FFFFFF", label: "White", params: params(19, 800, 1500, 6, 0.003) },
    { color: "#808080", label: "Gray", params: params(18, 800, 1000, 6, 0.001) },
    { color: "#800080", label: "Purple", params: params(18, 800, 367, 6, 0.002) },
    { color: "#0000FF", label: "Blue", params: params(18, 800, 500, 6, 0.002) },
    { color: "#008000", label: "Green", params: params(18, 800, 570, 6, 0.002) },
    { color: "#FFD000", label: "Yellow", params: params(25, 800, 200, 6, 0.002) },
    { color: "#FF8000", label: "Orange", params: params(20.4, 800, 266, 6, 0.002) },
    { color: "#FF0000", label: "Red", params: params(18, 800, 333, 6, 0.002) },
    { color: "#8B4513", label: "Brown", params: params(24.4, 700, 300, 60, 0.003) },
  ],
};

/** 60W MOPA / stainless starting table (independent source — very different numbers). */
const PRESET_60W: MaterialPreset = {
  name: "60W stainless",
  machine: "60W MOPA",
  material: "Stainless 304/316",
  colors: [
    { color: "#000000", label: "Black", params: params(55, 850, 233, 10, 0.03) },
    { color: "#FF0000", label: "Red", params: params(15, 500, 300, 50, 0.003) },
    { color: "#FFD000", label: "Yellow", params: params(50, 750, 200, 10, 0.005) },
    { color: "#0000FF", label: "Blue", params: params(50, 950, 200, 10, 0.005) },
  ],
};

export const MATERIAL_PRESETS: MaterialPreset[] = [PRESET_100W, PRESET_60W];

/** Snap an arbitrary ns value to the nearest standard MOPA Q-pulse step. */
export function nearestQPulseStep(ns: number): number {
  let best = Q_PULSE_STEPS[0];
  let bestD = Infinity;
  for (const step of Q_PULSE_STEPS) {
    const d = Math.abs(step - ns);
    if (d < bestD) {
      bestD = d;
      best = step;
    }
  }
  return best;
}

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace(/^#/, "");
  return [
    parseInt(h.slice(0, 2), 16) || 0,
    parseInt(h.slice(2, 4), 16) || 0,
    parseInt(h.slice(4, 6), 16) || 0,
  ];
}

function colorDistance(a: string, b: string): number {
  const [ar, ag, ab] = hexToRgb(a);
  const [br, bg, bb] = hexToRgb(b);
  const dr = ar - br;
  const dg = ag - bg;
  const db = ab - bb;
  return dr * dr + dg * dg + db * db;
}

/**
 * Pick the preset color entry closest (in RGB) to `color`. Returns a fresh copy
 * of its params (never a shared reference), or null when the preset is empty.
 */
export function nearestPresetParams(preset: MaterialPreset, color: string): CutParams | null {
  let best: CutParams | null = null;
  let bestD = Infinity;
  for (const entry of preset.colors) {
    const d = colorDistance(entry.color, color);
    if (d < bestD) {
      bestD = d;
      best = entry.params;
    }
  }
  return best ? { ...best } : null;
}

/** True when every preset color is a valid "#RRGGBB" and carries sane params. */
export function presetIsValid(preset: MaterialPreset): boolean {
  if (!preset.colors.length) return false;
  return preset.colors.every((c) => {
    if (!/^#[0-9A-Fa-f]{6}$/.test(c.color)) return false;
    const p = c.params;
    return (
      Number.isFinite(p.power) &&
      Number.isFinite(p.speed) &&
      Number.isFinite(p.frequencyKhz) &&
      Number.isFinite(p.qPulseNs) &&
      Number.isFinite(p.intervalMm) &&
      Number.isFinite(p.passes) &&
      p.power >= 0 &&
      p.speed > 0 &&
      p.frequencyKhz > 0 &&
      p.qPulseNs > 0 &&
      p.intervalMm > 0 &&
      p.passes >= 1
    );
  });
}
